import posthog from 'posthog-js';
import { trackGoogleAnalyticsEvent } from './googleAnalytics';

export const initPostHog = () => {
  if (typeof window !== 'undefined') {
    const apiKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
    const host = process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://app.posthog.com';
    
    if (!apiKey) {
      console.warn('PostHog API key not found. Analytics will not work.');
      return;
    }

    try {
      posthog.init(apiKey, {
        api_host: host,
        // Enable session recording with safety checks
        capture_pageview: true,
        capture_pageleave: true,
        
        // Session replay configuration - disable by default to avoid iframe issues
        disable_session_recording: true, // Changed from false to prevent iframe errors
        
        // Error tracking
        capture_heatmaps: true,
        
        // Privacy settings
        respect_dnt: true,
        opt_out_capturing_by_default: false,
        
        // Performance
        loaded: (posthog) => {
          if (process.env.NODE_ENV === 'development') {
            console.log('PostHog loaded successfully');
            // Enable debug mode in development
            posthog.debug();
          }
        },
        
        // Feature flags
        bootstrap: {
          // Add any bootstrap feature flags here if needed
        },
        
        // Autocapture configuration - disable problematic autocapture
        autocapture: {
          // Disable autocapture on forms to prevent iframe communication issues
          dom_event_allowlist: ['click', 'submit'],
          url_allowlist: [],
          element_allowlist: []
        },
        
        // Cross-domain tracking with iframe safety
        cross_subdomain_cookie: true,
        persistence: 'localStorage+cookie',
        
        // Disable surveys by default to prevent iframe errors
        disable_surveys: true,
        
        // Advanced options
        before_send: (event: any) => {
          try {
            // Remove sensitive data safely
            if (event.properties && typeof event.properties === 'object') {
              if (event.properties.password) delete event.properties.password;
              if (event.properties.email && process.env.NODE_ENV === 'production') {
                // Hash email in production for privacy
                event.properties.email_hash = btoa(event.properties.email);
                delete event.properties.email;
              }
            }
            return event;
          } catch (error) {
            console.warn('Error sanitizing PostHog properties:', error);
            return event;
          }
        },
        
        // IP capture (set to false for privacy compliance)
        ip: false,
        
        // Property blacklist (properties to never capture)
        property_blacklist: [
          'password',
          'credit_card',
          'ssn',
          'phone_number', // We might want to track this differently
        ]
      });
    } catch (error) {
      console.error('Failed to initialize PostHog:', error);
    }

    // Set up error tracking with better error handling
    if (typeof window !== 'undefined') {
      window.addEventListener('error', (event) => {
        try {
          // Avoid tracking PostHog's own errors or iframe communication errors
          if (event.message && (
            event.message.includes('relay-message-to-frames') ||
            event.message.includes('posthog') ||
            event.filename?.includes('posthog') ||
            event.message.includes('SCDynimacBridge') ||
            event.message.includes('Can\'t find variable: SC')
          )) {
            return; // Skip PostHog internal errors and third-party script errors
          }

          if (posthog && typeof posthog.capture === 'function') {
            posthog.capture('javascript_error', {
              error_message: event.message,
              error_filename: event.filename,
              error_lineno: event.lineno,
              error_colno: event.colno,
              error_stack: event.error?.stack,
              error_type: 'unhandled_error'
            });
          }
        } catch (err) {
          console.warn('Failed to track error with PostHog:', err);
        }
      });

      window.addEventListener('unhandledrejection', (event) => {
        try {
          // Avoid tracking PostHog's own rejection errors
          const reason = event.reason;
          if (reason && (
            String(reason).includes('relay-message-to-frames') ||
            String(reason).includes('posthog') ||
            String(reason).includes('SCDynimacBridge') ||
            String(reason).includes('Can\'t find variable: SC') ||
            (reason.message && reason.message.includes('s.id'))
          )) {
            return; // Skip PostHog internal errors and third-party script errors
          }

          if (posthog && typeof posthog.capture === 'function') {
            posthog.capture('javascript_error', {
              error_message: reason?.message || 'Unhandled promise rejection',
              error_stack: reason?.stack,
              error_type: 'unhandled_promise_rejection',
              error_reason: String(reason)
            });
          }
        } catch (err) {
          console.warn('Failed to track unhandled rejection with PostHog:', err);
        }
      });
    }
  }
};

// Enhanced tracking functions with error handling
export const trackEvent = (eventName: string, properties?: Record<string, any>) => {
  trackGoogleAnalyticsEvent(eventName, properties);

  if (typeof window !== 'undefined' && posthog && typeof posthog.capture === 'function') {
    try {
      posthog.capture(eventName, {
        ...properties,
        timestamp: new Date().toISOString(),
        page_url: window.location.href,
        page_title: document.title,
      });
    } catch (error) {
      console.warn('Failed to track event:', eventName, error);
    }
  }
};

export const trackError = (error: Error, context?: Record<string, any>) => {
  if (typeof window !== 'undefined' && posthog && typeof posthog.capture === 'function') {
    try {
      // Don't track PostHog's own errors
      if (error.message?.includes('relay-message-to-frames') || 
          error.message?.includes('s.id') ||
          error.stack?.includes('posthog')) {
        return;
      }

      posthog.capture('error', {
        error_message: error.message,
        error_stack: error.stack,
        error_name: error.name,
        error_type: 'tracked_error',
        ...context,
        timestamp: new Date().toISOString(),
        page_url: window.location.href,
      });
    } catch (err) {
      console.warn('Failed to track error with PostHog:', err);
    }
  }
};

export const identifyUser = (userId: string, properties?: Record<string, any>) => {
  if (typeof window !== 'undefined' && posthog) {
    posthog.identify(userId, properties);
  }
};

export const setUserProperties = (properties: Record<string, any>) => {
  if (typeof window !== 'undefined' && posthog) {
    posthog.people.set(properties);
  }
};

export const trackPageView = (url?: string) => {
  if (typeof window !== 'undefined' && posthog) {
    posthog.capture('$pageview', {
      $current_url: url || window.location.href,
    });
  }
};

// Feature flags
export const getFeatureFlag = (flagKey: string): boolean | string | undefined => {
  if (typeof window !== 'undefined' && posthog && typeof posthog.getFeatureFlag === 'function') {
    try {
      return posthog.getFeatureFlag(flagKey);
    } catch (error) {
      console.warn('Failed to get feature flag:', flagKey, error);
      return undefined;
    }
  }
  return undefined;
};

export const isFeatureEnabled = (flagKey: string): boolean => {
  if (typeof window !== 'undefined' && posthog && typeof posthog.isFeatureEnabled === 'function') {
    try {
      return posthog.isFeatureEnabled(flagKey) || false;
    } catch (error) {
      console.warn('Failed to check if feature enabled:', flagKey, error);
      return false;
    }
  }
  return false;
};

// Session replay with safety checks
export const startSessionRecording = () => {
  if (typeof window !== 'undefined' && posthog && typeof posthog.startSessionRecording === 'function') {
    try {
      // Check if we're in an iframe to avoid communication errors
      if (window.self !== window.top) {
        console.warn('Skipping session recording in iframe to prevent communication errors');
        return;
      }
      posthog.startSessionRecording();
    } catch (error) {
      console.warn('Failed to start session recording:', error);
    }
  }
};

export const stopSessionRecording = () => {
  if (typeof window !== 'undefined' && posthog && typeof posthog.stopSessionRecording === 'function') {
    try {
      posthog.stopSessionRecording();
    } catch (error) {
      console.warn('Failed to stop session recording:', error);
    }
  }
};

// Surveys - disabled to prevent iframe communication errors
export const showSurvey = (surveyId: string) => {
  if (typeof window !== 'undefined' && posthog) {
    try {
      // Check if we're in an iframe to avoid communication errors
      if (window.self !== window.top) {
        console.warn('Skipping survey in iframe to prevent communication errors');
        return;
      }
      
      // For PostHog surveys, we typically trigger them via feature flags
      // This is a simplified implementation - surveys are disabled by default
      console.log(`Survey ${surveyId} would be shown here (currently disabled)`);
    } catch (error) {
      console.warn('Survey functionality not available:', error);
    }
  }
};

// Group analytics (for events/organizations)
export const setGroup = (groupType: string, groupKey: string, properties?: Record<string, any>) => {
  if (typeof window !== 'undefined' && posthog) {
    posthog.group(groupType, groupKey, properties);
  }
};

// Custom tracking for invite app
export const trackInviteEvent = (action: string, inviteId: string, eventId?: string, properties?: Record<string, any>) => {
  trackEvent('invite_action', {
    action,
    invite_id: inviteId,
    event_id: eventId,
    ...properties
  });
};

export const trackInvalidLink = (originalUrl: string, source?: string, properties?: Record<string, any>) => {
  trackEvent('invalid_link_accessed', {
    original_url: originalUrl,
    source: source || 'direct',
    timestamp: new Date().toISOString(),
    ...properties
  });
};

export const trackEventAction = (action: string, eventId: string, properties?: Record<string, any>) => {
  trackEvent('event_action', {
    action,
    event_id: eventId,
    ...properties
  });
};

export const trackRSVPAction = (action: string, inviteId: string, eventId: string, status: string, properties?: Record<string, any>) => {
  trackEvent('rsvp_action', {
    action,
    invite_id: inviteId,
    event_id: eventId,
    rsvp_status: status,
    ...properties
  });
};

// Performance tracking
export const trackPerformance = (metric: string, value: number, properties?: Record<string, any>) => {
  trackEvent('performance_metric', {
    metric,
    value,
    ...properties
  });
};

// User journey tracking
export const trackUserJourney = (step: string, properties?: Record<string, any>) => {
  trackEvent('user_journey', {
    step,
    ...properties
  });
};

export default posthog;
